import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, FileText, ShieldCheck, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const textareaCls =
  "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5C542]/50 transition-colors font-mono leading-relaxed resize-y";

const DOCS = [
  { slug: "terms", label: "Termos de Uso", icon: FileText, publicPath: "/termos-de-uso" },
  { slug: "privacy", label: "Política de Privacidade", icon: ShieldCheck, publicPath: "/politica-de-privacidade" },
];

export const LegalDocsManager = () => {
  const [active, setActive] = useState("terms");
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDoc(null);
    api.get(`/admin/legal/${active}`).then(({ data }) => setDoc(data)).catch(() => toast.error("Erro ao carregar"));
  }, [active]);

  const patch = (fields) => setDoc((d) => ({ ...d, ...fields }));

  const updateSection = (idx, fields) => {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      sections[idx] = { ...sections[idx], ...fields };
      return { ...d, sections };
    });
  };

  const moveSection = (idx, dir) => {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      const j = idx + dir;
      if (j < 0 || j >= sections.length) return d;
      [sections[idx], sections[j]] = [sections[j], sections[idx]];
      return { ...d, sections };
    });
  };

  const removeSection = (idx) => {
    if (!window.confirm("Remover esta seção?")) return;
    setDoc((d) => ({ ...d, sections: (d.sections || []).filter((_, i) => i !== idx) }));
  };

  const addSection = () => {
    setDoc((d) => ({
      ...d,
      sections: [
        ...(d.sections || []),
        { id: String((d.sections?.length || 0) + 1), title: "Nova Seção", body: [""] },
      ],
    }));
  };

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const payload = {
        title: doc.title || "",
        company_name: doc.company_name || "",
        cnpj: doc.cnpj || "",
        website: doc.website || "",
        contact_email: doc.contact_email || "",
        updated_at_label: doc.updated_at_label || "",
        intro: doc.intro || "",
        sections: (doc.sections || []).map((s) => ({
          id: s.id || "",
          title: s.title || "",
          body: (typeof s.body === "string" ? s.body.split("\n") : s.body || []).filter((l) => l !== null && l !== undefined),
        })),
      };
      const { data } = await api.put(`/admin/legal/${active}`, payload);
      setDoc(data);
      toast.success("Documento salvo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const publicPath = DOCS.find((d) => d.slug === active)?.publicPath || "/";

  return (
    <div data-testid="admin-legal">
      <PageTitle
        title="Documentos Legais"
        subtitle="Edite Termos de Uso e Política de Privacidade — publicação imediata no site"
        action={
          <div className="flex gap-2 flex-wrap">
            <a
              href={publicPath}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-white/[0.04] border border-white/10 text-white/80 hover:border-[#F5C542]/40 hover:text-white transition-colors"
              data-testid="legal-preview"
            >
              <Eye className="w-4 h-4" /> Ver página pública <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <GoldButton onClick={save} disabled={!doc || saving} data-testid="legal-save">
              <Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar"}
            </GoldButton>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {DOCS.map((d) => {
          const Icon = d.icon;
          const activeState = active === d.slug;
          return (
            <button
              key={d.slug}
              onClick={() => setActive(d.slug)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors border ${
                activeState
                  ? "bg-[#F5C542]/10 text-[#F5C542] border-[#F5C542]/40"
                  : "bg-white/[0.03] text-white/60 border-white/10 hover:text-white"
              }`}
              data-testid={`legal-tab-${d.slug}`}
            >
              <Icon className="w-4 h-4" /> {d.label}
            </button>
          );
        })}
      </div>

      {!doc ? (
        <div className="text-white/40 text-sm">Carregando...</div>
      ) : (
        <div className="space-y-6">
          <Card>
            <h3 className="font-display font-bold text-white mb-4">Metadados</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Título">
                <input value={doc.title || ""} onChange={(e) => patch({ title: e.target.value })} className={inputCls} data-testid="legal-title" />
              </Field>
              <Field label="Data de atualização (rótulo)" hint="Exibido como 'Última atualização: ...'">
                <input value={doc.updated_at_label || ""} onChange={(e) => patch({ updated_at_label: e.target.value })} className={inputCls} data-testid="legal-updated" />
              </Field>
              <Field label="Razão Social">
                <input value={doc.company_name || ""} onChange={(e) => patch({ company_name: e.target.value })} className={inputCls} data-testid="legal-company" />
              </Field>
              <Field label="CNPJ">
                <input value={doc.cnpj || ""} onChange={(e) => patch({ cnpj: e.target.value })} className={inputCls} data-testid="legal-cnpj" />
              </Field>
              <Field label="Website">
                <input value={doc.website || ""} onChange={(e) => patch({ website: e.target.value })} className={inputCls} data-testid="legal-website" />
              </Field>
              <Field label="E-mail de contato">
                <input value={doc.contact_email || ""} onChange={(e) => patch({ contact_email: e.target.value })} className={inputCls} data-testid="legal-email" />
              </Field>
              <Field label="Introdução (opcional)" className="sm:col-span-2">
                <textarea
                  rows={3}
                  value={doc.intro || ""}
                  onChange={(e) => patch({ intro: e.target.value })}
                  className={textareaCls}
                  placeholder="Texto opcional antes das seções (aparece em um bloco no topo)"
                  data-testid="legal-intro"
                />
              </Field>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-white">Seções ({(doc.sections || []).length})</h3>
              <GhostButton onClick={addSection} data-testid="legal-add-section">
                <Plus className="w-4 h-4 inline mr-1" /> Adicionar seção
              </GhostButton>
            </div>
            <div className="space-y-5">
              {(doc.sections || []).map((s, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-4 sm:p-5 border border-white/[0.08] bg-white/[0.02]"
                  data-testid={`legal-section-${i}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
                      Seção #{i + 1}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveSection(i, -1)}
                        disabled={i === 0}
                        className="w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/70 disabled:opacity-30 flex items-center justify-center"
                        title="Mover para cima"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveSection(i, 1)}
                        disabled={i === (doc.sections || []).length - 1}
                        className="w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/70 disabled:opacity-30 flex items-center justify-center"
                        title="Mover para baixo"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeSection(i)}
                        className="w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-red-500/15 text-white/70 hover:text-red-400 flex items-center justify-center"
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-[100px_1fr] gap-3">
                    <Field label="ID">
                      <input value={s.id || ""} onChange={(e) => updateSection(i, { id: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Título">
                      <input value={s.title || ""} onChange={(e) => updateSection(i, { title: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                  <div className="mt-3">
                    <Field label="Conteúdo (um parágrafo por linha)" hint="Linhas iniciadas com • ou a)/b)/c) recebem indentação visual">
                      <textarea
                        rows={6}
                        value={Array.isArray(s.body) ? s.body.join("\n") : (s.body || "")}
                        onChange={(e) =>
                          updateSection(i, { body: e.target.value.split("\n") })
                        }
                        className={textareaCls}
                      />
                    </Field>
                  </div>
                </div>
              ))}
              {(doc.sections || []).length === 0 && (
                <div className="text-center text-white/40 text-sm py-6">
                  Nenhuma seção. Clique em "Adicionar seção".
                </div>
              )}
            </div>
          </Card>

          <div className="flex justify-end">
            <GoldButton onClick={save} disabled={saving} data-testid="legal-save-bottom">
              <Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar alterações"}
            </GoldButton>
          </div>
        </div>
      )}
    </div>
  );
};
